(() => {
  if (window.LenchoCartBridge?.ready) return;

  const drawerSelector = 'theme-drawer#cart-drawer';
  const sectionId = 'cart-drawer-section';
  const getDrawer = () => document.querySelector(drawerSelector);
  const rootPath = () => window.Shopify?.routes?.root || '/';
  let refreshPromise;

  const waitForDrawer = async () => {
    await customElements.whenDefined('theme-drawer').catch(() => {});
    return getDrawer();
  };

  const getCart = async () => {
    const response = await fetch(`${rootPath()}cart.js`, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Unable to read the cart.');
    return response.json();
  };

  const getFreshDrawerMarkup = async () => {
    const url = new URL(rootPath(), window.location.origin);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('_', String(Date.now()));
    const response = await fetch(url.toString(), {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    });
    if (!response.ok) throw new Error('Unable to refresh the cart drawer.');
    return response.text();
  };

  const updateCounts = (cart) => {
    const count = Number(cart?.item_count || 0);
    document.querySelectorAll('[data-cart-count], [data-lh-cart-count], [ref="cartBubbleCount"], .cart-bubble__text-count').forEach((node) => {
      node.textContent = count;
      if ('hidden' in node) node.hidden = count === 0 && node.matches('[data-lh-cart-count]');
    });
  };

  const refresh = async () => {
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
      const cart = await getCart();
      updateCounts(cart);

      // Shopify can return a pre-mutation section during a cart/add response.
      // Fetch the drawer separately and only accept markup matching the cart count.
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const html = await getFreshDrawerMarkup();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const nextDrawer = parsed.querySelector(drawerSelector);
        const drawerIsEmpty = Boolean(nextDrawer?.querySelector('.cart-drawer--empty'));
        if (Number(cart.item_count || 0) === 0 || !drawerIsEmpty || attempt === 2) {
          replaceMarkup(html);
          return cart;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 80));
      }
      return cart;
    })().finally(() => {
      refreshPromise = null;
    });
    return refreshPromise;
  };

  const open = async () => {
    try {
      await refresh();
    } catch (_) {}
    const drawer = await waitForDrawer();
    if (typeof drawer?.open !== 'function') return false;
    drawer.open();
    return true;
  };

  const openNow = async () => {
    const drawer = await waitForDrawer();
    if (typeof drawer?.open !== 'function') return false;
    drawer.open();
    return true;
  };

  const close = async () => {
    const drawer = await waitForDrawer();
    if (typeof drawer?.close !== 'function') return false;
    await drawer.close();
    return true;
  };

  // Keep the upgraded theme-drawer host alive. Replacing it breaks native
  // dialog state and the listeners that power quantity and close actions.
  const replaceMarkup = (html) => {
    if (!html) return false;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const nextDrawer = parsed.querySelector(drawerSelector);
    const currentDrawer = getDrawer();
    const currentPanel = currentDrawer?.querySelector('dialog[ref="panel"]');
    const nextPanel = nextDrawer?.querySelector('dialog[ref="panel"]');
    if (!currentDrawer || !currentPanel || !nextPanel) return false;

    for (const attribute of ['aria-labelledby', 'aria-modal', 'role', 'cart-summary-sticky']) {
      const value = nextPanel.getAttribute(attribute);
      if (value === null) currentPanel.removeAttribute(attribute);
      else currentPanel.setAttribute(attribute, value);
    }
    currentPanel.innerHTML = nextPanel.innerHTML;
    return true;
  };

  const handleClick = (event) => {
    const cartTrigger = event.target.closest?.('[data-lh-cart-link][aria-controls="cart-drawer"]');
    if (cartTrigger) {
      event.preventDefault();
      event.stopPropagation();
      open();
      return;
    }

    const closeButton = event.target.closest?.(`${drawerSelector} .theme-drawer__close-button`);
    if (closeButton) {
      event.preventDefault();
      event.stopPropagation();
      close();
    }
  };

  document.addEventListener('click', handleClick, true);

  window.LenchoCartBridge = {
    ready: true,
    getDrawer,
    open,
    openNow,
    close,
    refresh,
    replaceMarkup,
  };
})();
